// src/components/managed-sources/GA4BqExportAuthStep.tsx

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBrandGoogle,
  IconPlugConnected,
  IconPlus,
  IconSearch,
  IconX,
} from "@tabler/icons-react";

import { discoverGA4BqDatasets } from "@/api/managedSources";
import {
  listAuthorizations,
  listBigQueryProjects,
  type BqProject,
} from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import type { GA4BqExportDataset } from "@/types/managedSources";
import type { GA4BqExportDatasetEntry } from "@/types/managedSources";
import { useAuth } from "@/context/AuthContext";
import { BigQueryAuthorizationCreateModal } from "@/components/authorizations/BigQueryAuthorizationCreateModal";
import { MultiSelect } from "@/ui";

const CREATE_NEW_SENTINEL = "__create_new__";
const EMPTY_SENTINEL = "__empty__";

/** How many per-dataset errors are spelled out before the list is summarised. */
const MAX_LISTED_DATASET_ERRORS = 5;

/**
 * Only a Grant-access authorization can carry a Managed-models source: the
 * source stores the selected authorization as its query credential, so an
 * uploaded key would list the export here but fail every query on the warehouse.
 */
export function isGrantAccessAuthorization(a: AuthorizationDTO): boolean {
  const mode = a?.config?.access_mode;
  return mode === "runtime_sa" || mode === "iam_grant";
}

type Props = {
  sourceProjectId: string;
  sourceDatasets: GA4BqExportDatasetEntry[];
  authorizationId: string | null;
  onAuthorizationChange: (authId: string | null) => void;
  onConfigChange: (projectId: string, datasets: GA4BqExportDatasetEntry[]) => void;
  onValidated: (valid: boolean) => void;
};

export default function GA4BqExportAuthStep({
  sourceProjectId,
  sourceDatasets,
  authorizationId,
  onAuthorizationChange,
  onConfigChange,
  onValidated,
}: Props) {
  const { user } = useAuth();
  const isAccountAdmin =
    user?.activeAccount?.role === "OWNER" ||
    user?.activeAccount?.role === "ADMIN";

  // Authorizations
  const [auths, setAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);

  // Inline-create modal
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Projects
  const [projects, setProjects] = useState<BqProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Dataset prefix
  const [datasetPrefix, setDatasetPrefix] = useState("analytics_");

  // Discovery
  const [datasets, setDatasets] = useState<GA4BqExportDataset[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [hasDiscovered, setHasDiscovered] = useState(false);

  // Selected dataset IDs (for MultiSelect)
  const selectedDatasetIds = sourceDatasets.map((d) => d.dataset_id);

  // Only Grant-access authorizations can be offered here.
  const usableAuths = auths.filter(isGrantAccessAuthorization);

  // Load BigQuery authorizations
  useEffect(() => {
    async function load() {
      setLoadingAuths(true);
      try {
        const list = await listAuthorizations({ type: "BIGQUERY" });
        setAuths(list);
        const usable = list.filter(isGrantAccessAuthorization);
        if (usable.length === 1) {
          onAuthorizationChange(usable[0].id);
        }
      } catch {
        // Non-fatal
      } finally {
        setLoadingAuths(false);
      }
    }
    load();
  }, []);

  // Load projects when auth changes
  useEffect(() => {
    if (!authorizationId) {
      setProjects([]);
      return;
    }
    async function loadProjects() {
      setLoadingProjects(true);
      setProjectsError(null);
      setProjects([]);
      try {
        const list = await listBigQueryProjects(authorizationId!);
        setProjects(list);
        if (list.length === 1) {
          onConfigChange(list[0].project_id, []);
        }
      } catch (e: any) {
        setProjectsError(e?.message || "Failed to load projects");
      } finally {
        setLoadingProjects(false);
      }
    }
    loadProjects();
  }, [authorizationId]);

  // Validation
  useEffect(() => {
    onValidated(sourceDatasets.length > 0 && !!sourceProjectId);
  }, [sourceDatasets, sourceProjectId, onValidated]);

  function resetDiscovery() {
    setDatasets([]);
    setTruncated(false);
    setHasDiscovered(false);
    setDiscoverError(null);
  }

  function handleAuthSelectChange(value: string | null) {
    if (value === CREATE_NEW_SENTINEL) {
      // Defensive admin re-check (frontend role is advisory; backend is source of truth)
      if (!isAccountAdmin) return;
      setCreateModalOpen(true);
      return;
    }
    if (value === EMPTY_SENTINEL) return;
    handleAuthChange(value);
  }

  function handleAuthChange(authId: string | null) {
    onAuthorizationChange(authId);
    onConfigChange("", []);
    setProjects([]);
    resetDiscovery();
  }

  // Fire-and-forget: the modal closes synchronously after calling onCreated.
  async function handleAuthCreated(newAuth?: AuthorizationDTO) {
    // Reset downstream parent state FIRST so stale options don't flash
    onConfigChange("", []);
    setProjects([]);
    resetDiscovery();

    let refreshedUsable: AuthorizationDTO[] | null = null;
    try {
      const refreshed = await listAuthorizations({ type: "BIGQUERY" });
      setAuths(refreshed);
      refreshedUsable = refreshed.filter(isGrantAccessAuthorization);
    } catch (e) {
      console.error(
        "Failed to refresh auth list after create — falling back to optimistic append",
        e,
      );
      if (newAuth) {
        setAuths((prev) =>
          prev.some((a) => a.id === newAuth.id) ? prev : [...prev, newAuth],
        );
      }
    }
    if (newAuth?.id && typeof newAuth.id === "string") {
      onAuthorizationChange(newAuth.id);
    } else if (refreshedUsable && refreshedUsable.length === 1) {
      onAuthorizationChange(refreshedUsable[0].id);
    }
  }

  function handleProjectChange(projectId: string | null) {
    onConfigChange(projectId || "", []);
    resetDiscovery();
  }

  async function handleDiscover() {
    if (!sourceProjectId || !authorizationId) return;
    setDiscovering(true);
    setDiscoverError(null);
    setDatasets([]);
    setTruncated(false);
    setHasDiscovered(false);
    onConfigChange(sourceProjectId, []);

    try {
      const prefix = datasetPrefix.trim() || undefined;
      const result = await discoverGA4BqDatasets(
        authorizationId,
        sourceProjectId,
        prefix,
      );
      setDatasets(result.datasets);
      setTruncated(result.truncated);
      setHasDiscovered(true);
      // Default: every dataset we can actually read and that has tables. A
      // dataset that errored or is empty stays selectable by hand.
      const autoSelect = result.datasets.filter(
        (d) => !d.error && d.table_count > 0,
      );
      if (autoSelect.length > 0) {
        onConfigChange(
          sourceProjectId,
          autoSelect.map((d) => ({ dataset_id: d.dataset_id, property_id: d.property_id })),
        );
      }
    } catch (e: any) {
      // Verbatim: the backend names the exact service account and project.
      setDiscoverError(e?.message || "Failed to discover datasets");
    } finally {
      setDiscovering(false);
    }
  }

  const datasetOptions = datasets.map((d) => ({
    value: d.dataset_id,
    label: `${d.dataset_id} (${d.table_count} tables, ${d.location})${
      d.error ? " — can't be read" : ""
    }`,
  }));

  const failedDatasets = datasets.filter((d) => d.error);

  // Re-selecting must not throw away a label the user already typed for that
  // dataset (select-all and search both rebuild the list from `datasets`).
  function toEntry(ds: GA4BqExportDataset): GA4BqExportDatasetEntry {
    const existing = sourceDatasets.find((s) => s.dataset_id === ds.dataset_id);
    return {
      dataset_id: ds.dataset_id,
      property_id: ds.property_id,
      ...(existing?.property_name ? { property_name: existing.property_name } : {}),
    };
  }

  function handleSelectionChange(values: string[]) {
    const selected = values
      .map((dsId) => {
        const ds = datasets.find((d) => d.dataset_id === dsId);
        return ds ? toEntry(ds) : null;
      })
      .filter(Boolean) as GA4BqExportDatasetEntry[];
    onConfigChange(sourceProjectId, selected);
  }

  function handleLabelChange(datasetId: string, value: string) {
    onConfigChange(
      sourceProjectId,
      sourceDatasets.map((d) =>
        d.dataset_id === datasetId ? { ...d, property_name: value } : d,
      ),
    );
  }

  // Select All covers the readable datasets only — an unreadable one is a
  // deliberate, per-dataset choice.
  const selectableDatasets = datasets.filter((d) => !d.error);
  const allSelected =
    selectableDatasets.length > 0 &&
    selectableDatasets.every((d) => selectedDatasetIds.includes(d.dataset_id));

  function toggleSelectAll() {
    if (allSelected) {
      onConfigChange(sourceProjectId, []);
    } else {
      onConfigChange(sourceProjectId, selectableDatasets.map(toEntry));
    }
  }

  // Build options: usable auths, plus EMPTY_SENTINEL when there are none,
  // plus CREATE_NEW_SENTINEL only for admins.
  const authOptions: Array<{ value: string; label: string; disabled?: boolean }> = [];
  if (usableAuths.length === 0) {
    authOptions.push({ value: EMPTY_SENTINEL, label: "—", disabled: true });
  } else {
    for (const a of usableAuths) {
      authOptions.push({ value: a.id, label: a.name });
    }
  }
  if (isAccountAdmin) {
    authOptions.push({
      value: CREATE_NEW_SENTINEL,
      label: "+ Create new BigQuery authorization",
    });
  }

  const projectOptions = projects.map((p) => ({
    value: p.project_id,
    label: p.display_name || p.project_id,
  }));

  if (loadingAuths) {
    return (
      <Stack align="center" justify="center" py={60}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Loading authorizations...</Text>
      </Stack>
    );
  }

  return (
    <>
    <Stack gap="lg">
      {/* Hero section */}
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="orange">
          <IconBrandGoogle size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect GA4 BigQuery Export
          </Text>
          <Text size="sm" c="dimmed">
            Transform your existing GA4 BigQuery export data into analytics-ready
            tables.
          </Text>
        </Stack>
      </Group>

      {/* No authorizations + non-admin: hint to escalate to admin */}
      {!isAccountAdmin && auths.length === 0 && (
        <Alert color="yellow">
          No BigQuery authorizations yet. Ask an account admin to create one in{" "}
          <Text span fw={600}>Settings &rarr; Authorizations</Text>.
        </Alert>
      )}

      {/* Select project + discover datasets */}
      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text fw={600} size="sm">
            Select GA4 datasets
          </Text>

            <Select
              label="BigQuery authorization"
              placeholder="Select an authorization"
              data={authOptions}
              value={authorizationId}
              onChange={handleAuthSelectChange}
              size="sm"
              searchable={usableAuths.length > 5}
              renderOption={({ option }) => {
                const opt = option as { value: string; label: string };
                if (opt.value === CREATE_NEW_SENTINEL) {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        width: "100%",
                        borderTop: "1px solid var(--mantine-color-gray-3)",
                        marginTop: 4,
                        paddingTop: 8,
                        color: "var(--mantine-color-green-7)",
                        fontWeight: 500,
                      }}
                    >
                      <IconPlus size={14} />
                      <span>Create new BigQuery authorization</span>
                    </div>
                  );
                }
                if (opt.value === EMPTY_SENTINEL) {
                  return (
                    <span
                      style={{
                        color: "var(--mantine-color-gray-5)",
                        fontStyle: "italic",
                      }}
                    >
                      — no authorizations yet —
                    </span>
                  );
                }
                return <span>{opt.label}</span>;
              }}
            />

            {auths.length > 0 && usableAuths.length === 0 && (
              <Text size="xs" c="dimmed">
                Uploaded-key authorizations can&apos;t be used for Managed models
                — create one with Grant access.
              </Text>
            )}

            {authorizationId && (
              <Select
                label="GCP Project"
                placeholder={loadingProjects ? "Loading projects..." : "Select a project"}
                data={projectOptions}
                value={sourceProjectId || null}
                onChange={handleProjectChange}
                disabled={loadingProjects || projects.length === 0}
                size="sm"
                searchable={projects.length > 5}
                rightSection={loadingProjects ? <Loader size={14} /> : undefined}
              />
            )}

            {projectsError && (
              <Alert color="red" icon={<IconX size={16} />}>
                {projectsError}
              </Alert>
            )}

            {authorizationId && !loadingProjects && projects.length === 0 && !projectsError && (
              <Text size="sm" c="dimmed">
                This authorization has no accessible BigQuery projects.
              </Text>
            )}

            {sourceProjectId && (
              <>
                <TextInput
                  label="Dataset prefix"
                  placeholder="analytics_"
                  description="Only datasets matching this prefix will be shown (default: analytics_)"
                  value={datasetPrefix}
                  onChange={(e) => {
                    setDatasetPrefix(e.currentTarget.value);
                    resetDiscovery();
                    onConfigChange(sourceProjectId, []);
                  }}
                  size="sm"
                />

                <Button
                  variant="light"
                  leftSection={<IconSearch size={16} />}
                  onClick={handleDiscover}
                  loading={discovering}
                  disabled={!sourceProjectId || !authorizationId}
                  size="sm"
                >
                  Discover datasets
                </Button>
              </>
            )}

            {discoverError && (
              <Alert color="red" icon={<IconX size={16} />}>
                {discoverError}
              </Alert>
            )}

            {hasDiscovered && datasets.length === 0 && !discoverError && (
              <Alert color="orange">
                No datasets found matching prefix <code>{datasetPrefix || "analytics_"}</code> in
                this project. Make sure your GA4 property has BigQuery linking enabled
                and the dataset name matches the prefix.
              </Alert>
            )}

            {truncated && (
              <Alert color="orange">
                More than 200 matching datasets were found — only the first 200
                are shown. Narrow the dataset prefix to find the rest.
              </Alert>
            )}

            {datasets.length > 0 && (
              <>
                <Group justify="space-between" align="center">
                  <Text size="sm" fw={500}>
                    {datasets.length} dataset{datasets.length !== 1 ? "s" : ""} found
                  </Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={toggleSelectAll}
                  >
                    {allSelected ? "Deselect All" : "Select All"}
                  </Button>
                </Group>
                <MultiSelect
                  label="GA4 Datasets"
                  placeholder="Search and select datasets"
                  data={datasetOptions}
                  value={selectedDatasetIds}
                  onChange={handleSelectionChange}
                  searchable
                  size="sm"
                />

                {failedDatasets.length > 0 && (
                  <Alert color="red" icon={<IconX size={16} />}>
                    <Text size="sm" fw={500}>
                      Some datasets could not be read
                    </Text>
                    {failedDatasets
                      .slice(0, MAX_LISTED_DATASET_ERRORS)
                      .map((d) => (
                        <Text key={d.dataset_id} size="xs">
                          {d.dataset_id}: {d.error}
                        </Text>
                      ))}
                    {failedDatasets.length > MAX_LISTED_DATASET_ERRORS && (
                      <Text size="xs">
                        &hellip;and{" "}
                        {failedDatasets.length - MAX_LISTED_DATASET_ERRORS} more
                      </Text>
                    )}
                  </Alert>
                )}

                {sourceDatasets.length > 0 && (
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Property names (optional)
                    </Text>
                    <Text size="xs" c="dimmed">
                      Give each property a name to recognise it in the property
                      filter on your Google Analytics 4 dashboard.
                    </Text>
                    {sourceDatasets.map((d) => (
                      <TextInput
                        key={d.dataset_id}
                        label={d.dataset_id}
                        placeholder="Label (optional)"
                        value={d.property_name ?? ""}
                        onChange={(e) =>
                          handleLabelChange(d.dataset_id, e.currentTarget.value)
                        }
                        size="sm"
                        styles={{ label: { fontFamily: "monospace", fontWeight: 400 } }}
                      />
                    ))}
                  </Stack>
                )}
              </>
            )}
        </Stack>
      </Paper>

      {/* Connected confirmation */}
      {sourceDatasets.length > 0 && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: "var(--mantine-color-green-4)",
            backgroundColor: "var(--mantine-color-green-0)",
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={32} color="green" variant="light" radius="xl">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                {sourceDatasets.length} of {datasets.length || sourceDatasets.length} datasets selected
              </Text>
              <Text size="xs" c="dimmed">
                {sourceDatasets.map((d) => d.dataset_id).join(", ")}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>

    <BigQueryAuthorizationCreateModal
      opened={createModalOpen}
      onClose={() => setCreateModalOpen(false)}
      backendUrl={import.meta.env.VITE_API_URL}
      isAccountAdmin={isAccountAdmin}
      onCreated={handleAuthCreated}
      grantOnly
    />
    </>
  );
}
